import { inject, Injectable } from '@angular/core';
import { AppConfig } from '@app/config/config.type';
import { GoogleTagManagerService, SawDust } from '@cudirect/shared-ui-library';
import { LenderConfiguration } from '@shared/model/lender-config.model';
import { GoogleTrackingConfiguration } from '@shared/model/google-tracking.model';

/**
 * Centralized service for managing Google Analytics and Google Tag Manager configurations.
 *
 * Handles dual-tracking setup where both Origence (global) and Lender (per-client)
 * analytics run simultaneously. GTM takes precedence over GA when both are configured for Lender.
 * Origence *only* uses GTM for their analytics, not standalone GA.
 * Origence configuration is read from `window.google` (set in index.html)
 * Lender configuration is provided via LenderConfiguration object.
 *
 * @example
 * ```typescript
 * // Initialize tracking services
 * googleTrackingService.initGoogleServices(lenderConfig, appConfig);
 *
 * // Publish events to both tracking systems
 * googleTrackingService.publishEvent('page_view', { page: '/home' });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class GoogleTrackingService {
  private logger = inject(SawDust).withContext({ component: 'GoogleTrackingService' });

  /** Stores the complete tracking configuration for both Origence and Lender systems */
  private trackingConfig?: GoogleTrackingConfiguration;

  constructor(private readonly gtm: GoogleTagManagerService) {}

  /**
   * Initializes Google Analytics and/or Google Tag Manager for the lender.
   * Prioritizes Google Tag Manager if enabled and configured, otherwise falls back to Google Analytics.
   * Creates and stores tracking configuration for both Origence and Lender systems.
   *
   * @param lenderConfig - Lender-specific configuration including GA/GTM settings
   * @param appConfig - Application configuration containing default GTM settings and fallbacks
   * @throws Does not throw but logs warnings for configuration issues
   */
  public initGoogleServices(lenderConfig: LenderConfiguration, appConfig: AppConfig): void {
    // Create and store the complete tracking configuration for both Origence and Lender
    this.trackingConfig = this.createTrackingConfiguration(lenderConfig);

    // Setup Lender tracking - prioritize GTM over GA
    if (this.isLenderGTMConfigured(lenderConfig)) this.setupLenderGTM(this.trackingConfig, lenderConfig, appConfig);
    else if (this.isLenderGAConfigured(lenderConfig)) this.setupLenderGA(this.trackingConfig);
    else this.logger.info('No Google tracking configured for this lender. Only Origence tracking will be available.');
  }

  /**
   * Creates the complete tracking configuration object for both Origence and Lender analytics.
   * Retrieves Origence configuration from window.google (set in index.html)
   * Builds Lender configuration from the provided lender config.
   *
   * @param lenderConfig - Lender-specific configuration containing GA/GTM settings
   * @returns Complete tracking configuration with both Origence and Lender settings
   */
  private createTrackingConfiguration(lenderConfig: LenderConfiguration): GoogleTrackingConfiguration {
    // Extract Origence GTM configuration from window object (injected via index.html)
    const windowGoogle = (window as { google?: { tagManagerId?: string; dataLayer?: string } }).google;
    const origenceGtmId = windowGoogle?.tagManagerId;
    const origenceDataLayer = windowGoogle?.dataLayer;

    // Log error if Origence GTM configuration is missing
    if (!origenceGtmId || !origenceDataLayer)
      this.logger.error('Origence GTM ID or Data Layer not found in window.google. Origence tracking will be unavailable.');

    return {
      origence: {
        dataLayer: origenceDataLayer, // Allows undefined - Origence tracking will be skipped if missing
        gtmId: origenceGtmId, // Allows undefined - Origence tracking will be skipped if missing
        gaId: null // Origence GA comes through GTM, not as separate implementation
      },
      lender: {
        dataLayer: 'dataLayer', // Lenders use the standard GTM data layer name
        gaId: this.isLenderGAConfigured(lenderConfig) ? lenderConfig.googleAnalytics.accountId : null,
        gtmId: this.isLenderGTMConfigured(lenderConfig) ? lenderConfig.googleTagManager.accountId : null
      }
    };
  }

  /**
   * Checks if Google Tag Manager is properly configured for the lender.
   * Validates that GTM is enabled AND has a valid account ID.
   *
   * @param lenderConfig - Lender configuration to check for GTM settings
   * @returns True if GTM is enabled and has an account ID, false otherwise
   */
  private isLenderGTMConfigured(lenderConfig: LenderConfiguration): boolean {
    return lenderConfig?.googleTagManager && lenderConfig?.googleTagManager?.isEnabled && !!lenderConfig?.googleTagManager?.accountId;
  }

  /**
   * Checks if Google Analytics is properly configured for the lender.
   * Validates that GA is enabled AND has a valid account ID.
   *
   * @param lenderConfig - Lender configuration to check for GA settings
   * @returns True if GA is enabled and has an account ID, false otherwise
   */
  private isLenderGAConfigured(lenderConfig: LenderConfiguration): boolean {
    return lenderConfig?.googleAnalytics && lenderConfig?.googleAnalytics?.isEnabled && !!lenderConfig?.googleAnalytics?.accountId;
  }

  /**
   * Sets up Google Analytics for the lender.
   * Only configures lender GA - Origence analytics come through their own GTM container.
   * This method assumes GA is already validated as configured.
   *
   * @param trackingConfig - Complete tracking configuration containing GA settings
   */
  private setupLenderGA(trackingConfig: GoogleTrackingConfiguration): void {
    // Configure lender GA with standard data layer - Origence uses custom data layer
    // GA ID is guaranteed to exist since isLenderGAConfigured was called first
    this.gtm.addGA(trackingConfig.lender.gaId, null, trackingConfig.lender.dataLayer);
  }

  /**
   * Sets up Google Tag Manager for the lender with allow/block list restrictions.
   * Configures lender GTM container with security restrictions and proper data layer.
   *
   * Special logic: If BOTH allowList and blockList from lenderConfig are empty/null
   * (i.e., the user hasn't done anything in Admin), then use appConfig blockList and
   * set allow to empty. The appConfig blockList should contain security defaults to
   * prevent custom HTML/JS execution.
   *
   * @param trackingConfig - Complete tracking configuration containing GTM settings
   * @param lenderConfig - Lender configuration for custom allow/block lists
   * @param appConfig - Application configuration containing default GTM settings and fallbacks
   * @remarks The way default allow/blocklists are handled is a bit confusing - see notes above
   * @see https://developers.google.com/tag-platform/tag-manager/restrict
   */
  private setupLenderGTM(trackingConfig: GoogleTrackingConfiguration, lenderConfig: LenderConfiguration, appConfig: AppConfig): void {
    let allow: string;
    let block: string;

    /** Check if lender has configured a custom allowList (non-empty string) */
    const hasCustomAllowList = lenderConfig?.googleTagManager?.allowList;
    /** Check if lender has configured a custom blockList (non-empty string) */
    const hasCustomBlockList = lenderConfig?.googleTagManager?.blockList;

    // I'm being very verbose here, so that folks can wrap their head around the logic - see notes above
    if (!hasCustomAllowList && !hasCustomBlockList) {
      // Case 1: No custom lender anything - use default blocklist and empty allowlist
      block = appConfig?.lenderGTM?.blockList || ''; // Let's watch the fallback here, maybe we want html,customScripts
      allow = '';
    } else if (hasCustomAllowList && hasCustomBlockList) {
      // Case 2: Both custom allowList AND blockList - use only lender values
      allow = lenderConfig.googleTagManager.allowList;
      block = lenderConfig.googleTagManager.blockList;
    } else if (hasCustomAllowList) {
      // Case 3: Only custom allowList - use only custom allowList (default blocklist isn't meant for this case)
      allow = lenderConfig.googleTagManager.allowList;
      block = '';
    } else if (hasCustomBlockList) {
      // Case 4: Only custom blockList - use default allowList + custom blockList
      block = lenderConfig.googleTagManager.blockList;
      allow = appConfig?.lenderGTM?.allowList || '';
    }

    // Configure lender GTM with standard data layer - Origence uses custom data layer
    // GTM ID is guaranteed to exist since isLenderGTMConfigured was called first
    this.gtm.addGTM(trackingConfig.lender.gtmId, trackingConfig.lender.dataLayer, null, allow, block);
  }

  /**
   * Publishes an event to both Origence and Lender data layers when appropriate.
   * This method provides centralized event publishing that automatically handles
   * dual-tracking without requiring callers to manage multiple data layers.
   *
   * @param event - Name of the event to publish (e.g., 'page_view', 'button_click')
   * @param data - Custom data payload to send with the event
   * @throws Does not throw but logs warnings if tracking is not initialized
   *
   * @example
   * ```typescript
   * // Publish a page view event
   * service.publishEvent('page_view', { page: '/home', userId: '123' });
   *
   * // Publish a custom interaction event
   * service.publishEvent('button_click', { button: 'submit', form: 'contact' });
   * ```
   *
   * @remarks
   * - Requires initGoogleServices() to be called first
   * - Events are sent to Origence data layer if both dataLayer and gtmId are configured
   * - Events are sent to Lender data layer only if dataLayer exists and either gaId or gtmId is configured
   * - GTM takes priority over GA when both are configured for the lender
   * - Gracefully handles missing data layers without throwing errors
   * - Provides comprehensive tracking across both analytics systems when appropriate
   */
  public publishEvent<T>(event: string, data: T): void {
    if (!this.trackingConfig) {
      this.logger.warn('Tracking configuration not initialized. Call initGoogleServices first.');
      return;
    }

    // Send event to Lender data layer (if configured)
    if (this.trackingConfig.lender.dataLayer) {
      if (this.trackingConfig.lender.gtmId) {
        // Lender has GTM configured, prioritize GTM event publishing
        this.gtm.publishEvent(event, data, this.trackingConfig.lender.dataLayer);
      } else if (this.trackingConfig.lender.gaId) {
        // Lender has GA configured, use GA event publishing via publishAnalyticsEvent method
        this.gtm.publishAnalyticsEvent(event, data, this.trackingConfig.lender.dataLayer);
      }
      // Note: If neither gtmId nor gaId is set, no lender event is published
    }

    // Send event to Origence data layer (if configured)
    if (this.trackingConfig.origence.dataLayer && this.trackingConfig.origence.gtmId) {
      this.gtm.publishEvent(event, data, this.trackingConfig.origence.dataLayer);
    }

    // Capture as event telemetry
    this.logger.logEvent(event, data);
  }
}
